import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { sessionQuery } from "@/features/auth/queries";
import {
  commentThreadQuery,
  commentViewerOf,
  presetCommentsQuery,
} from "@/features/comments/queries";
import { PresetScreen } from "@/features/presets/components/PresetScreen";
import { cachedPresetListing, presetDetailQuery } from "@/features/presets/queries";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute("/_authenticated/presets/$slug/")({
  /**
   * `comment` names a comment to show first, as a permalink does. `version` names a published
   * version to show in place of the live one.
   */
  validateSearch: (search: Record<string, unknown>): { comment?: string; version?: string } => ({
    comment:
      typeof search.comment === "string" && UUID.test(search.comment) ? search.comment : undefined,
    version:
      typeof search.version === "string" && UUID.test(search.version) ? search.version : undefined,
  }),
  // Live pages open on cached listings; pinned pages load their exact version first.
  loaderDeps: ({ search }) => ({ highlightedCommentId: search.comment, version: search.version }),
  loader: async ({
    context: { queryClient },
    params: { slug },
    deps: { highlightedCommentId, version },
  }) => {
    const pinned = version
      ? await queryClient.ensureQueryData(presetDetailQuery(slug, version))
      : undefined;
    if (!version) void queryClient.prefetchQuery(presetDetailQuery(slug));
    void queryClient
      .ensureQueryData(sessionQuery)
      .then(session => {
        const viewer = commentViewerOf(session);
        return Promise.all([
          queryClient.prefetchInfiniteQuery(presetCommentsQuery(slug, "top", viewer)),
          highlightedCommentId &&
            queryClient.prefetchQuery(commentThreadQuery(highlightedCommentId, viewer)),
        ]);
      })
      .catch(() => undefined);
    return { known: pinned?.preset ?? cachedPresetListing(queryClient, slug) };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.known ? `${loaderData.known.title} · Pax.co` : "Scenario · Pax.co" },
    ],
  }),
  component: PresetPage,
});

function PresetPage() {
  const { slug } = Route.useParams();
  const { comment, version } = Route.useSearch();
  const queryClient = useQueryClient();
  const { data: detail } = useQuery(presetDetailQuery(slug, version));
  const known = useMemo(() => cachedPresetListing(queryClient, slug), [queryClient, slug]);

  // Keyed by slug: another scenario is another screen, not this one with new
  // words in it. Without it React keeps the state of the screen the reader
  // left — including a map that had been opened, which would then load and
  // tessellate the new scenario's world into a card nobody has opened.
  return (
    <PresetScreen
      key={`${slug}:${version ?? "live"}`}
      slug={slug}
      storefront={detail ?? (version ? undefined : known)}
      detail={detail}
      highlightedCommentId={comment}
      versionId={version}
    />
  );
}
